import { exactCount, exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, NoteBlock, TableBlock } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import {
  acknowledgeElevationForm, requestElevationForm, reviewElevationForm,
} from './accesscontrols'
import type { Caller } from './handles'
import { callerBeside } from './writes'

/**
 * Access & Audit › Break-glass (`ac-break`), read from
 * `GET /v1/admin/break-glass` (go-wallet#235, handler
 * `internal/server/admin_access.go`).
 *
 * # The one place law 18 is deliberately broken, so it needs a screen
 *
 * Law 18 says a gated control is shown disabled with the required role named,
 * never hidden. Break-glass is the single exception: controls behind it are
 * HIDDEN until a session is live. An exception like that needs a screen that
 * documents it and records who elevated, why, for how long, what they did and
 * who read it back afterwards. An elevation path with no session record is
 * worse than no elevation path at all.
 *
 * # An elevation confers nothing, and this screen has to say so
 *
 * This is not what "break-glass" usually means, and getting it wrong in either
 * direction is serious. The endpoint is explicit:
 *
 *   > AND AN ACKNOWLEDGED ELEVATION CONFERS NOTHING EITHER… It adds no role and
 *   > widens no permission: every 403 on this surface is identical with a live
 *   > session and without one. What it does is attach its session id to every
 *   > audit row written while it is open, so the post-hoc review is a query
 *   > rather than an archaeology of timestamps.
 *
 * So the screen must not read as "elevated access". An operator who believes a
 * live session widens what they may do will read their next 403 as a broken
 * elevation rather than as the answer it is, and an auditor who believes it
 * will look for a privilege escalation that never happened.
 *
 * # State is the server's, never a timer here
 *
 * `state` is computed server-side against one instant, and the expiry is stored
 * as `started_at + time_box`. A console counting down is a suggestion; two
 * consoles counting down disagree. Nothing here computes whether a session is
 * still live — it renders what the backend said and stamps when it asked.
 *
 * # A request nobody seconded is a row, not a gap
 *
 * `pending_acknowledgement` sessions and expired ones are in the log
 * deliberately: an elevation nobody seconded, and one that expired unused, are
 * both facts about how the emergency went. `awaiting_review` counts the closed
 * ones nobody read back, which is the number an auditor actually asks for —
 * an elevation nobody reviewed is an emergency nobody assessed.
 *
 * # Read-only, and the elevation control is NOT drawn as one
 *
 * `POST /break-glass`, `/break-glass/{id}/acknowledge` and `/{id}/review` all
 * exist and none is called. The screen renders the registry the backend serves
 * — the reason codes and the allowed time boxes — as what a request WILL
 * require, so the shape of the control is legible before it is built. A picker
 * that offered a value the server refuses is the thing serving the registry
 * prevents; a picker that submits nothing is not that risk, and this does not
 * even draw one.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors the BreakGlassSession schema
// ---------------------------------------------------------------------------

export interface BreakGlassView {
  readonly id: number
  readonly subject: string
  readonly staff_id: number
  readonly display_name?: string
  readonly reason_code: string
  readonly reason: string
  readonly time_box_minutes: number
  readonly state: string
  readonly requested_by: string
  readonly requested_at: string
  readonly acknowledged_by?: string
  readonly acknowledged_at?: string
  readonly expires_at?: string
  readonly actions?: number
  readonly reviewed_by?: string
  readonly reviewed_at?: string
  readonly review_finding?: string
  readonly review_note?: string
  readonly awaiting_review: boolean
}

export interface BreakGlassResponse {
  readonly elevations: readonly unknown[]
  readonly awaiting_review?: unknown
  readonly reason_codes?: readonly unknown[]
  readonly time_boxes?: readonly unknown[]
  readonly as_of?: unknown
}

export const BREAK_GLASS_PATH = '/break-glass'

const SOURCE = 'postgres · break_glass_elevations (GET /v1/admin/break-glass)'
const AUTHORITY = 'operational · state computed server-side'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}


/**
 * An elevation id, count or duration this console can state exactly.
 *
 * `exactCount`, and the change from `Number.isInteger` is not cosmetic here
 * (#207). The id this parses becomes a PATH SEGMENT — `elevationPath` puts it
 * in `POST /break-glass/{id}/acknowledge` and `/review`, which are the two
 * halves of a maker-checker act worked under incident pressure. An id past 2^53
 * has already been rounded by `JSON.parse`, and `Number.isInteger` says yes to
 * the rounded value: the console would then acknowledge, or refuse to
 * acknowledge, a record that is not the one on the screen.
 *
 * The `>= 0` it already had is kept — a negative elevation id or a negative
 * count of actions is a value the backend cannot have meant.
 */
const whole = exactCount

interface Elevation {
  readonly id: number | null
  readonly subject: string
  readonly displayName: string
  readonly reasonCode: string
  readonly reason: string
  readonly timeBox: number | null
  readonly state: string
  readonly requestedBy: string
  readonly requestedAt: string
  readonly acknowledgedBy: string
  readonly expiresAt: string
  /** `null` before the session opened. Zero is a finding, not an absence. */
  readonly actions: number | null
  readonly reviewedBy: string
  readonly reviewedAt: string
  readonly finding: string
  /** Three-state: the backend's boolean, or `null` when it did not arrive. */
  readonly awaitingReview: boolean | null
}

function readElevation(raw: unknown): Elevation | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const e = raw as Partial<BreakGlassView>
  return {
    id: whole(e.id),
    subject: text(e.subject),
    displayName: text(e.display_name),
    reasonCode: text(e.reason_code),
    reason: text(e.reason),
    timeBox: whole(e.time_box_minutes),
    state: text(e.state),
    requestedBy: text(e.requested_by),
    requestedAt: text(e.requested_at),
    acknowledgedBy: text(e.acknowledged_by),
    expiresAt: text(e.expires_at),
    actions: whole(e.actions),
    reviewedBy: text(e.reviewed_by),
    reviewedAt: text(e.reviewed_at),
    finding: text(e.review_finding),
    awaitingReview: typeof e.awaiting_review === 'boolean' ? e.awaiting_review : null,
  }
}

/** `2026-07-27T03:04:00Z` → `2026-07-27 03:04 UTC`. Absence stays absent. */
function stamp(at: string): string {
  return at === '' ? '' : `${at.slice(0, 10)} ${at.slice(11, 16)} UTC`
}

/**
 * The state chip.
 *
 * `live` is the loud one — an elevation open right now is the thing an operator
 * scanning this screen is looking for. A state this console does not know reads
 * as unknown in alarm tone rather than as one of the three, because guessing
 * which of live/expired an unrecognised value means is the difference between
 * "somebody is in there now" and "somebody was".
 */
function stateChip(e: Elevation): ChipCell {
  if (e.state === 'live') return ['LIVE', 'r']
  if (e.state === 'pending_acknowledgement') return ['awaiting a second admin', 'a']
  if (e.state === 'expired') return ['expired', 'n']
  return [e.state === '' ? '— not stated' : `unknown: ${e.state}`, 'r']
}

/**
 * The review cell.
 *
 * `awaiting_review` is what an auditor asks about, so it is a chip rather than
 * a blank. A session with a `questioned` finding stays loud permanently: the
 * point of a post-hoc review is that somebody read it back and said what they
 * thought, and a questioned elevation reading the same as an approved one
 * discards the only output the review has.
 */
function reviewChip(e: Elevation): ChipCell {
  if (e.finding === 'questioned') return ['QUESTIONED', 'r']
  if (e.finding === 'appropriate') {
    const when = e.reviewedAt === '' ? '' : ` ${e.reviewedAt.slice(0, 10)}`
    return [`reviewed${when}`, 'g']
  }
  if (e.awaitingReview === true) return ['awaiting review', 'a']
  if (e.awaitingReview === false) return ['not closed yet', 'n']
  return ['— not stated', 'r']
}

const COLS: readonly Column[] = [
  ['ID', 'm', '72px'],
  ['Elevated', 's', 'minmax(170px,1.2fr)'],
  ['Reason', 'x', 'minmax(220px,1.6fr)'],
  ['Requested', 'm', '150px'],
  ['Time box', 'n', '92px'],
  ['Actions', 'n', '86px'],
  ['State', 'c', '160px'],
  ['Post-hoc review', 'c', '160px'],
]

function row(e: Elevation): readonly Cell[] {
  return [
    e.id === null ? '— no id' : String(e.id),
    [e.displayName === '' ? '— no name recorded' : e.displayName, e.subject],
    // The code is what a quarterly review groups by; the sentence is what it
    // reads. Both, because either alone is half the record.
    e.reasonCode === '' && e.reason === ''
      ? '— not recorded'
      : `${e.reasonCode === '' ? '— no code' : e.reasonCode} · ${e.reason === '' ? '— no sentence' : e.reason}`,
    stamp(e.requestedAt) === '' ? '— not returned' : stamp(e.requestedAt),
    e.timeBox === null ? '—' : `${String(e.timeBox)} min`,
    // Zero is a finding — "they elevated and did nothing" — so it renders as 0
    // and only a genuinely absent count renders as a dash.
    e.actions === null ? '—' : String(e.actions),
    stateChip(e),
    reviewChip(e),
  ]
}

function logTable(rows: readonly Elevation[], unreadable: number): TableBlock {
  const live = rows.filter((e) => e.state === 'live').length
  const head = `${String(rows.length)} elevation(s)${live === 0 ? '' : ` · ${String(live)} LIVE now`}`
  return {
    t: 'table',
    title: 'break_glass_elevations',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: unreadable === 0 ? head : `${head} · ${String(unreadable)} row(s) could not be read`,
    foot2:
      'Requests nobody seconded and sessions that expired unused are here too — how an emergency went includes the elevations that went nowhere. State is computed by the backend against one instant and is never a timer in this console.',
  }
}

const CONFERS_NOTHING: Block = {
  t: 'note',
  tone: 'a',
  kicker: 'Read this before the table',
  title: 'An elevation grants nothing — not a role, not a permission',
  body: 'This is not what break-glass usually means, and it matters in both directions. Every refusal on this surface is identical with a live session and without one. What an elevation does is attach its id to every audit row written while it is open, so a post-hoc review is a query rather than a reconstruction from timestamps. An operator who thinks a live session widens their access will read their next refusal as a broken elevation; an auditor who thinks so will look for an escalation that never happened.',
  ref: 'a request confers nothing either — the clock starts when a SECOND admin acknowledges it',
}

const LAW_18_EXCEPTION: Block = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Law 18 · the exception',
  title: 'Controls behind break-glass are hidden, not disabled',
  body: 'Everywhere else in this console a permission-gated control is shown disabled with the required role named, so staff can see that an escalation path exists. Break-glass is the one exception: its controls are hidden until a session is live. That is why this screen exists — an exception to the rule that makes paths visible needs a record of who used it, why, for how long, what they did, and who read it back.',
  ref: 'brief §14 · the whole console changes appearance while a session is active',
}

/**
 * The two-person rule, said once for the screen rather than per control.
 *
 * Both halves bar the same person — whoever requested the elevation — and both
 * are refused by the server as well. Stating it here means the disabled
 * controls below are read as one rule rather than as three separate problems.
 */
const TWO_PEOPLE: Block = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Two people, always',
  title: 'Whoever asks for an elevation cannot open it, and cannot review it either',
  body: 'A request is opened by a SECOND admin and read back afterwards by somebody other than the requester. Both rules are enforced by the server — and the review one is enforced twice, in the handler and in the database, so it survives a refactor. The controls below say which of them applies to you before you compose anything, rather than after.',
  ref: 'a self-acknowledgement and a self-review are both refused',
}

function emptyNote(): Block {
  return {
    t: 'note',
    tone: 'g',
    kicker: 'Break-glass',
    title: 'Nobody has elevated',
    body: 'The absence of rows is the good state, and on this screen it is the only place in the console where an empty table is reassuring rather than suspicious. It says no emergency needed an elevation in what the log covers — not that elevation is unavailable.',
    ref: 'break_glass_elevations · the read returned no rows',
  }
}

function unreadableRowsNote(count: number): Block {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Partial read',
    title: `${String(count)} elevation(s) could not be read`,
    body: 'Counted rather than dropped. This log is read to answer "who went in, and did anybody check afterwards" — a page that quietly omits a session answers it wrongly, and there is no other record that would catch the omission.',
    ref: 'the shape of those rows is not one this console knows',
  }
}

function strings(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  return value.filter((v): v is string => typeof v === 'string' && v !== '')
}

/**
 * A list of ids, dropping any this console cannot state exactly.
 *
 * Dropped rather than rounded, for the reason in `exact.ts`: a rounded id is a
 * different record, and a list that silently carried one would be a list an
 * operator counts and acts on.
 */
function numbers(value: unknown): readonly number[] {
  if (!Array.isArray(value)) return []
  return value.map(exactInteger).filter((v): v is number => v !== null)
}

/** The client and the caller, or `null` on a surface that only reads. */
export interface Writer {
  readonly api: ApiClient
  readonly caller: Caller
}

export function composeBreakGlass(
  res: BreakGlassResponse, readAt: Date, writer: Writer | null = null,
): ScreenData {
  const rows: Elevation[] = []
  let unreadable = 0
  for (const raw of res.elevations) {
    const parsed = readElevation(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const awaitingReview = whole(res.awaiting_review)
  const asOf = text(res.as_of)

  const blocks: Block[] = [CONFERS_NOTHING, logTable(rows, unreadable)]
  if (unreadable > 0) blocks.push(unreadableRowsNote(unreadable))
  if (rows.length === 0 && unreadable === 0) blocks.push(emptyNote())

  const unreviewed = unreviewedFinding(awaitingReview)
  if (unreviewed !== null) blocks.push(unreviewed)
  blocks.push(LAW_18_EXCEPTION, TWO_PEOPLE)

  // The request control belongs to the screen; acknowledge and review belong
  // to a row, because both act on one elevation and both refuse a different
  // person depending on who requested THAT one.
  if (writer !== null) {
    blocks.push(requestElevationForm(writer.api, writer.caller))
    for (const e of rows) {
      if (e.id === null) continue
      const standing = {
        id: e.id,
        subject: e.subject,
        state: e.state,
        requestedBy: e.requestedBy,
        // Three-state on the read, two here, collapsed in the SAFE direction:
        // an elevation whose review flag did not arrive is not offered for
        // review, rather than offered and refused by the server.
        awaitingReview: e.awaitingReview === true,
        actions: e.actions,
      }
      if (e.state === 'pending_acknowledgement') {
        blocks.push(acknowledgeElevationForm(writer.api, writer.caller, standing))
      }
      if (standing.awaitingReview) {
        blocks.push(reviewElevationForm(writer.api, writer.caller, standing))
      }
    }
  }

  const missing: string[] = []
  if (awaitingReview === null) missing.push('how many closed elevations are unreviewed')
  if (strings(res.reason_codes).length === 0) missing.push('the reason-code registry')
  if (numbers(res.time_boxes).length === 0) missing.push('the allowed time boxes')

  const readStamp = asOf === '' ? readAtStamp(readAt) : stamp(asOf)
  const provenance: DataSource =
    missing.length === 0
      ? { kind: 'live', source: SOURCE, authority: AUTHORITY, readAt: readStamp }
      : { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readStamp, missing }

  return {
    blocks,
    provenance,
    rowKeys: rows.map((e) => (e.id === null ? null : String(e.id))),
  }
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Break-glass',
      title: 'The elevation log could not be read',
      body: `${reason} Whether anybody is elevated right now is not known from this read, and neither is whether any closed session is still unreviewed. That is different from nobody having elevated — which is the reading an empty screen would invite.`,
      ref: decisionRef === null
        ? 'No elevation was read. This is not an empty log.'
        : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function isBreakGlassResponse(payload: unknown): payload is BreakGlassResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<BreakGlassResponse>).elevations)
}

export const loadBreakGlass: ScreenLoader = async (api) => {
  const calling = callerBeside(api)
  let payload: unknown
  try {
    payload = await api.get<unknown>(BREAK_GLASS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The break-glass endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isBreakGlassResponse(payload)) {
    return unreadableScreen(
      'The break-glass endpoint returned a shape this console does not know.',
      null,
    )
  }
  return composeBreakGlass(payload, new Date(), { api, caller: await calling })
}

/**
 * Closed elevations nobody reviewed, stated as a finding.
 *
 * # Why this outlived the panel it came from
 *
 * It was one row of a `kv` block titled "The log, as read", beside rows
 * counting what was live right now and what had been reviewed and questioned.
 * Both of those are countable from the table above it; this one is not — it is
 * reported over the WHOLE log rather than this page, which is exactly why it
 * could not be derived from the rows on screen.
 *
 * An elevation nobody reviewed is an emergency nobody assessed. That is a
 * finding, so it renders only when there is something to find: a zero draws
 * nothing rather than a green chip, because a reassurance printed on every
 * healthy read is one an operator stops seeing.
 *
 * `null` — the backend did not send the figure — is the more serious of the
 * two and is toned accordingly. It is not a zero, and reading it as one would
 * report an unaudited emergency as a clean log.
 */
function unreviewedFinding(awaitingReview: number | null): NoteBlock | null {
  if (awaitingReview === null) {
    return {
      t: 'note',
      tone: 'r',
      kicker: 'Unreviewed',
      title: 'The backend did not say how many closed elevations are unreviewed',
      body: 'This is not a count of zero.',
    }
  }
  if (awaitingReview === 0) return null
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Unreviewed',
    title: `${String(awaitingReview)} closed elevation(s) have not been reviewed`,
    body: 'Counted over the whole log, not this page.',
  }
}
